import { useCallback } from "react";
import { useSDK } from "./sdk-context";

export class AdminAuthError extends Error {
  constructor() {
    super("admin_unauthorized");
    this.name = "AdminAuthError";
  }
}

function bounceToLogin(): void {
  const from = window.location.hash || "#/";
  window.location.hash = `#/login?redirect=${encodeURIComponent(from)}`;
}

/**
 * Fetch wrapper for admin endpoints. Auth is the admin_session httpOnly cookie
 * set by POST /api/auth/login for role='admin' tenant users (PR #44 unified
 * login). Cookies ride the fetch automatically same-origin; there is no
 * shared-secret header. On 401 we bounce to the unified login page.
 */
export function useAdminFetch() {
  const { context } = useSDK();
  return useCallback(
    async function adminFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
      const headers = new Headers(init.headers ?? {});
      headers.set("x-heady-team-id", context?.teamId ?? "standalone-team");
      headers.set("x-heady-user-id", context?.userId ?? "standalone-user");
      if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
      const res = await fetch(path, { ...init, headers });
      const ct = res.headers.get("content-type") || "";
      if (res.status === 401) {
        bounceToLogin();
        throw new AdminAuthError();
      }
      if (!res.ok) {
        let msg = res.statusText;
        try {
          if (ct.includes("json")) {
            const j = (await res.json()) as { message?: string; error?: string };
            msg = j.message || j.error || msg;
          }
        } catch {}
        throw new Error(`${res.status}: ${msg}`);
      }
      if (ct.includes("json")) return res.json() as Promise<T>;
      return undefined as T;
    },
    [context]
  );
}
